import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from './AdminAuthContext';
import './css/AdminPanel.css';

const API_BASE = '';
const API = `${API_BASE}/api`;

function AdminPanel() {
  const { adminToken } = useAdminAuth();

  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [hostPassword, setHostPassword] = useState('');
  const [selectedChain, setSelectedChain] = useState('');
  const [setupChains, setSetupChains] = useState([
    { setupKey: 'terra_classic', prettyName: 'Terra Classic', symbol: 'LUNC' },
    { setupKey: 'luna', prettyName: 'Terra', symbol: 'LUNA' },
  ]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${adminToken}` }), [adminToken]);

  const loadAccounts = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/admin/accounts`, { headers });
      setAccounts(response.data?.accounts || []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load registered accounts.');
    } finally {
      setLoading(false);
    }
  }, [adminToken, headers]);

  useEffect(() => {
    axios.get(`${API_BASE}/api/cosmos/chains`, { params: { setupStatus: 'automated' } })
      .then((response) => {
        const chains = response.data?.chains || [];
        if (chains.length) setSetupChains(chains);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const filteredAccounts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return accounts;
    return accounts.filter((account) => [
      account.firstName,
      account.lastName,
      account.email,
      account.accountType,
      account.chainName,
      account.assignedIp,
    ].filter(Boolean).join(' ').toLowerCase().includes(term));
  }, [accounts, search]);

  const selectAccount = (account) => {
    setSelectedAccount(account);
    setIpAddress(account.assignedIp || '');
    setHostPassword('');
    setSelectedChain(account.chainName || '');
    setError('');
    setSuccess('');
  };

  const formatDate = (value) => value
    ? new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    : 'Not available';

  const accountStatus = (account) => {
    if (account.accountType === 'admin') return 'Administrator';
    if (account.chainName && account.assignedIp && account.hostPasswordConfigured) return 'Ready';
    return 'Needs setup';
  };

  const handleAssignIpAndPassword = async () => {
    try {
      setError('');
      setSuccess('');

      if (!selectedAccount || selectedAccount.accountType !== 'user') {
        setError('Select a client user account');
        return;
      }

      if (!ipAddress) {
        setError('Please enter an IP address');
        return;
      }

      if (!hostPassword && !selectedAccount.hostPasswordConfigured) {
        setError('Please enter a host password');
        return;
      }

      if (!selectedChain) {
        setError('Please select a chain');
        return;
      }

      const response = await axios.put(
        `${API}/admin/assign-ip`,
        {
          email: selectedAccount.email,
          assignedIp: ipAddress,
          hostPassword,
          chainName: selectedChain,
        },
        {
          headers,
          withCredentials: true,
        }
      );

      setSuccess(response.data?.message || 'Server assignment saved');
      const updatedAccount = {
          ...selectedAccount,
          assignedIp: ipAddress,
          hostPasswordConfigured: Boolean(hostPassword) || selectedAccount.hostPasswordConfigured,
          chainName: selectedChain,
      };
      setSelectedAccount(updatedAccount);
      setAccounts((current) => current.map((account) => (
        account.accountType === 'user' && account.id === updatedAccount.id ? updatedAccount : account
      )));
      setHostPassword('');
    } catch (err) {
      console.error('Error assigning IP and password:', err);
      console.error('Response:', err?.response?.data);
      setError(
        err?.response?.data?.message ||
        'Failed to assign IP and password'
      );
    }
  };

  return (
    <main className="admin-panel-page">
      <header className="admin-panel-head">
        <div>
          <p className="admin-panel-kicker">Account administration</p>
          <h1>Registered Accounts</h1>
          <p>View every registered person and manage validator access for client accounts.</p>
        </div>
        <div className="admin-account-counts" aria-label="Account totals">
          <span><strong>{accounts.filter((account) => account.accountType === 'user').length}</strong> clients</span>
          <span><strong>{accounts.filter((account) => account.accountType === 'admin').length}</strong> admins</span>
        </div>
      </header>

      {error && <p className="admin-panel-message error" role="alert">{error}</p>}
      {success && <p className="admin-panel-message success" role="status">{success}</p>}

      <section className="admin-directory">
        <div className="admin-directory-toolbar">
          <div>
            <h2>People</h2>
            <span>{filteredAccounts.length} of {accounts.length} accounts</span>
          </div>
          <div className="admin-directory-actions">
            <input
              type="search"
              value={search}
              placeholder="Search name, email, chain or server"
              aria-label="Search registered accounts"
              onChange={(event) => setSearch(event.target.value)}
            />
            <button type="button" onClick={loadAccounts} disabled={loading}>Refresh</button>
          </div>
        </div>

        <div className="admin-account-table-wrap">
          <table className="admin-account-table">
            <thead><tr><th>Person</th><th>Role</th><th>Chain</th><th>Server</th><th>Status</th><th>Registered</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {!loading && filteredAccounts.length === 0 && <tr><td colSpan="7" className="admin-empty">No matching accounts.</td></tr>}
              {filteredAccounts.map((account) => (
                <tr key={`${account.accountType}-${account.id}`} className={selectedAccount?.accountType === account.accountType && selectedAccount?.id === account.id ? 'selected' : ''}>
                  <td data-label="Person"><strong>{[account.firstName, account.lastName].filter(Boolean).join(' ') || 'Unnamed account'}</strong><span>{account.email}</span></td>
                  <td data-label="Role"><span className={`account-role ${account.accountType}`}>{account.accountType === 'admin' ? 'Admin' : 'Client'}</span></td>
                  <td data-label="Chain">{account.chainName || 'Not assigned'}</td>
                  <td data-label="Server">{account.assignedIp || 'Not assigned'}</td>
                  <td data-label="Status"><span className={`account-status ${accountStatus(account).toLowerCase().replace(' ', '-')}`}>{accountStatus(account)}</span></td>
                  <td data-label="Registered">{formatDate(account.createdAt)}</td>
                  <td data-label="Actions"><button type="button" className="account-manage-button" onClick={() => selectAccount(account)}>{account.accountType === 'user' ? 'Manage' : 'View'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <p className="admin-loading">Loading registered accounts...</p>}
        </div>
      </section>

      {selectedAccount && (
        <section className="admin-account-detail">
          <div className="admin-detail-summary">
            <p className="admin-panel-kicker">Selected account</p>
            <h2>{[selectedAccount.firstName, selectedAccount.lastName].filter(Boolean).join(' ')}</h2>
            <dl>
              <div><dt>Email</dt><dd>{selectedAccount.email}</dd></div>
              <div><dt>Role</dt><dd>{selectedAccount.accountType === 'admin' ? 'Administrator' : 'Client user'}</dd></div>
              <div><dt>Terms accepted</dt><dd>{selectedAccount.termsAccepted ? 'Yes' : 'No'}</dd></div>
              <div><dt>Registered</dt><dd>{formatDate(selectedAccount.createdAt)}</dd></div>
              {selectedAccount.accountType === 'user' && <div><dt>Host password</dt><dd>{selectedAccount.hostPasswordConfigured ? 'Configured' : 'Not configured'}</dd></div>}
              {selectedAccount.accountType === 'user' && <div><dt>Validator operator</dt><dd>{selectedAccount.terravaloper || 'Not created'}</dd></div>}
            </dl>
          </div>

          {selectedAccount.accountType === 'user' ? (
            <form className="admin-assignment-form" onSubmit={(event) => { event.preventDefault(); handleAssignIpAndPassword(); }}>
              <div>
                <p className="admin-panel-kicker">Validator access</p>
                <h2>Chain and server assignment</h2>
              </div>
              <label htmlFor="admin-chain">Chain</label>
              <select id="admin-chain" value={selectedChain} onChange={(event) => setSelectedChain(event.target.value)} required>
                <option value="">Select a chain</option>
                {setupChains.map((chain) => <option key={chain.setupKey} value={chain.setupKey}>{chain.prettyName} ({chain.symbol})</option>)}
              </select>
              <label htmlFor="admin-ip">Server IP address</label>
              <input id="admin-ip" type="text" value={ipAddress} placeholder="Server IP address" onChange={(event) => setIpAddress(event.target.value)} required />
              <label htmlFor="admin-host-password">Host password</label>
              <input id="admin-host-password" type="password" value={hostPassword} placeholder={selectedAccount.hostPasswordConfigured ? 'Leave blank to keep the current password' : 'Enter host password'} onChange={(event) => setHostPassword(event.target.value)} />
              <button type="submit">Save assignment</button>
            </form>
          ) : (
            <div className="admin-role-note">
              <h2>Administrator account</h2>
              <p>Administrator accounts manage the platform and do not receive validator server assignments.</p>
            </div>
          )}
        </section>
      )}
    </main>
  );
}

export default AdminPanel;
